import React, { useState, useEffect } from 'react';
import Typography from 'apollo-react/components/Typography';
import LinearProgress from 'apollo-react/components/LinearProgress';
import Box from 'apollo-react/components/Box';

/**
 * Global download progress state management
 * Simple pub/sub pattern for tracking download progress across components
 */
const downloadProgressStates = new Map();
const downloadSubscribers = new Set();

const notifyDownloadSubscribers = () => {
  downloadSubscribers.forEach(callback => callback());
};

/**
 * Update download progress for a specific file
 * @param {string} fileId - Unique identifier for the download file
 * @param {object} state - Download state { downloading: boolean, progress: number, fileName?: string }
 */
export const updateDownloadProgress = (fileId, state) => {
  downloadProgressStates.set(fileId, state);
  notifyDownloadSubscribers();
};

/**
 * Get current download progress for a file
 * @param {string} fileId - File identifier
 * @returns {object} Current progress state
 */
const getDownloadProgress = (fileId) => {
  return downloadProgressStates.get(fileId) || { downloading: false, progress: 0 };
};

/**
 * Download Progress Indicator Component
 * Shows download progress with a clean, professional design
 * Only renders when actively downloading
 */
const DownloadProgress = ({ fileId, fileName }) => {
  const [progressState, setProgressState] = useState(() => getDownloadProgress(fileId));
  
  useEffect(() => {
    const handleUpdate = () => {
      const newState = getDownloadProgress(fileId);
      setProgressState(newState);
    };
    
    downloadSubscribers.add(handleUpdate);
    
    return () => {
      downloadSubscribers.delete(handleUpdate);
    };
  }, [fileId]);
  
  // Hide when not downloading
  if (!progressState.downloading) {
    return null;
  }
  
  return (
    <Box sx={{ 
      marginTop: 1,
      marginBottom: 1,
      padding: 2,
      backgroundColor: '#e8f4fd',
      borderRadius: 1,
      border: '1px solid #b3d7ff'
    }}>
      <Typography 
        variant="body2" 
        sx={{ 
          marginBottom: 1, 
          color: '#1976d2',
          fontWeight: 500,
          fontSize: '0.875rem'
        }}
      >
        Downloading {progressState.fileName || fileName || 'file'}... {Math.round(progressState.progress || 0)}%
      </Typography>
      
      <LinearProgress 
        variant="determinate" 
        value={progressState.progress || 0} 
        sx={{ 
          height: 6,
          borderRadius: 1,
          backgroundColor: '#e3f2fd',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#1976d2',
            borderRadius: 1,
          }
        }}
      />
    </Box>
  );
};

export default DownloadProgress;
